import React, { useEffect, useState, useRef, useMemo } from "react";
import {
  Table,
  Button,
  Dropdown,
  Menu,
  Switch,
  Tooltip,
  Modal,
  Form,
  Input,
  Row,
  Col,
  Space,
} from "antd";
import {
  MoreOutlined,
  ReloadOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import classNames from "classnames";
import { useSharedState } from "../../../Shared/Services/WindowSizeService";
import { ServiceUtils } from "../../../Shared/Utils/ServiceUtils";
import { alertService } from "../../../Shared/Utils/ToasterUtils";
import { get, map } from "lodash";
import CryptoJS from "crypto-js";
import { getDecryptSession } from "../../../Shared/Utils/CommonUtils";

const UserManagement = () => {
  // const { Option } = Select;
  const navigate = useNavigate();
  const windowSize$ = useSharedState();

  const [userDetails, setUserDetails] = useState({});
  const [userManagementTableData, setUserManagementTableData] = useState({
    headerContent: [],
    bodyContent: [],
  });
  const [state, setState] = useState({
    data: [],
    searchTex: "",
  });
  const [EnablePasswordModal, setEnablePasswordModal] = useState(false);
  const [enableSSOLogin, setEnableSSOLogin] = useState(false);
  const [ssoState, setSsoState] = useState({});
  const [pendingSSO, setPendingSSO] = useState({ userId: null, value: false });
  const [currentUserId, setCurrentUserId] = useState(null);
  const [tableKey, setTableKey] = useState(0);
  const [openRest, setOpenReset] = useState(false);

  const [form] = Form.useForm();

  // ---------------------- MENU ----------------------
  const menu = (record) => (
    <Menu>
      <Menu.Item key="edit" onClick={() => handleActions(record, "edit")}>
        Edit
      </Menu.Item>
    </Menu>
  );

  const handleActions = (data, action) => {
    if (action === "edit") {
      navigate(`/admin/user/${data?.id}`);
    }
  };

  // ---------------------- TABLE DATA FETCH ----------------------
  const getUserTableDetails = () => {
    ServiceUtils.getRequest("usersList").then(
      (response) => {
        if (response.status === 200 && response?.data.status) {
          const res = response.data?.data;

          // SSO state object
          const ssoObj = {};
          res?.bodyContent?.forEach((user) => {
            ssoObj[user.id] = user.ssoEnabled;
          });
          setSsoState(ssoObj);

          // Customize columns
          const headerColumns = res?.headerContent?.map((el) => {
            if (el.key === "passwordEnabled") {
              el.render = (_, record) => (
                <div style={{ textAlign: "center" }}>
                  <Switch
                    checked={record.passwordEnabled}
                    onChange={(val) =>
                      handleDropdownChange(val, el.key, record.id, true)
                    }
                  />
                  {record.passwordEnabled ? (
                    <Tooltip title={"Reset password"}>
                      <ReloadOutlined
                        style={{ marginLeft: 15 }}
                        className="pointer"
                        onClick={() => openPaswordModal(record)}
                      />
                    </Tooltip>
                  ) : (
                    <ReloadOutlined
                      style={{ marginLeft: 15, opacity: 0.5 }}
                      className="disabled-cursor"
                    />
                  )}
                </div>
              );
            } else if (el.key === "ssoEnabled") {
              el.render = (_, record) => (
                <div style={{ textAlign: "center" }}>
                  <Switch
                    checked={ssoObj[record.id]}
                    onChange={(checked) => {
                      setPendingSSO({ userId: record.id, value: checked });
                      setEnableSSOLogin(true);
                    }}
                  />
                </div>
              );
            } else if (el.key === "action") {
              el.render = (_, record) => (
                <Dropdown overlay={menu(record)} trigger={["click"]}>
                  <Button type="link" icon={<MoreOutlined />} />
                </Dropdown>
              );
            } else if (el.key === "email") {
              el.render = (_, record) => (
                <Space size="middle">
                  <Tooltip title={record?.email}>{record?.email}</Tooltip>
                </Space>
              );
            }
            return el;
          });

          setUserManagementTableData({
            headerContent: headerColumns,
            bodyContent: res.bodyContent,
          });

          setState({
            data: res.bodyContent,
            searchTex: "",
          });
        } else if (response.status === 401) {
          navigate("/logout");
        } else {
          alertService.error(response.data.message);
        }
      },
      (error) => {
        console.log(error);
        alertService.error("Error while fetching data");
      }
    );
  };

  useEffect(() => {
    const session = localStorage.getItem("arcutis-user");
    const userDetails = getDecryptSession(session);
    setUserDetails(userDetails);
    if (userDetails.admin_access) {
      getUserTableDetails();
    }
  }, []);

  // ---------------------- SEARCH ----------------------
  const onSearch = (e) => {
    const value = e.target.value;
    const reg = new RegExp(value, "gi");

    const filteredData = userManagementTableData.bodyContent.filter((record) =>
      [record.firstName, record.lastName, record.email, record.role].some(
        (field) => field?.match(reg)
      )
    );

    setState({
      ...state,
      searchTex: value,
      data: value ? filteredData : [...userManagementTableData.bodyContent],
    });
  };

  // ---------------------- PASSWORD SWITCH / SSO SWITCH ----------------------
  const handleDropdownChange = (value, key, id, withUpdate) => {
    if (key === "passwordEnabled" && value === false && withUpdate) {
      enablePasswordService(key, id, value);
      return;
    }

    const updatedBody = userManagementTableData.bodyContent.map((row) =>
      row.id === id ? { ...row, [key]: value } : row
    );

    setUserManagementTableData((prev) => ({
      ...prev,
      bodyContent: updatedBody,
    }));

    setState((prev) => ({
      ...prev,
      data: updatedBody,
    }));

    if (key === "passwordEnabled") setEnablePasswordModal(value);
  };

  const enablePasswordService = (key, id, value) => {
    const payload = {
      userData: {
        [key]: value,
      },
      type: "edit",
      userId: id,
    };
    ServiceUtils.postRequest("userOps", payload).then(
      (response) => {
        if (response.status === 200 && response?.data.status) {
          getUserTableDetails();
        } else if (response.status === 401) {
          navigate("/logout");
        } else {
          alertService.error(response.data.message);
        }
      },
      (error) => {
        console.log(error);
        alertService.error("Error while updating password");
      }
    );
  };

  const openPaswordModal = (rec) => {
    setOpenReset(true);
    setCurrentUserId(rec?.id);
  };

  const confirmSSOLogin = () => {
    const payload = {
      userData: {
        ssoEnabled: pendingSSO.value,
      },
      type: "edit",
      userId: pendingSSO.userId,
    };

    ServiceUtils.postRequest("userOps", payload).then(
      (response) => {
        if (response.status === 200 && response?.data.status) {
          setSsoState((prev) => ({
            ...prev,
            [pendingSSO.userId]: pendingSSO.value,
          }));
          setEnableSSOLogin(false);
          getUserTableDetails();
        } else if (response.status === 401) {
          navigate("/logout");
        } else {
          alertService.error(response.data.message);
        }
      },
      (error) => {
        console.log(error);
        alertService.error("Error while updating SSO");
      }
    );
  };

  const cancelSSOLogin = () => {
    setSsoState((prev) => ({
      ...prev,
      [pendingSSO.userId]: !pendingSSO.value,
    }));
    setEnableSSOLogin(false);
  };

  // ---------------------- PASSWORD MODAL ----------------------
  const submitPassword = () => {
    const userInfo = { ...form.getFieldsValue() };

    const encryptPassword = (pass) => {
      const key = "ThisIsASecureKey1234567890123456";
      const iv = "ThisIsAnIV123456";
      return CryptoJS.AES.encrypt(
        pass,
        CryptoJS.enc.Utf8.parse(key),
        { iv: CryptoJS.enc.Utf8.parse(iv), mode: CryptoJS.mode.CBC, padding: CryptoJS.pad.Pkcs7 }
      ).toString();
    };

    if (userInfo.password) userInfo.password = encryptPassword(userInfo.password);
    if (userInfo.oldPassword) userInfo.oldPassword = encryptPassword(userInfo.oldPassword);
    if (userInfo.email) userInfo.email += "@arcutis.com";

    const payload = {
      userData: {
        password: userInfo.password,
        oldPassword: userInfo.oldPassword,
        passwordEnabled: true,
      },
      type: "edit",
      userId: currentUserId,
    };

    ServiceUtils.postRequest("userOps", payload).then(
      (response) => {
        if (response.status === 200 && response?.data.status) {
          setOpenReset(false);
          getUserTableDetails();
          form.resetFields();
        } else if (response.status === 401) {
          navigate("/logout");
        } else {
          alertService.error(response.data.message);
        }
      },
      (error) => {
        console.log(error);
        alertService.error("Error while saving password");
      }
    );
  };

  const SubmitButton = ({ form, children }) => {
    const [submittable, setSubmittable] = useState(false);
    const values = Form.useWatch([], form);

    useEffect(() => {
      form
        .validateFields({ validateOnly: true })
        .then(() => setSubmittable(true))
        .catch(() => setSubmittable(false));
    }, [form, values]);

    return (
      <Button
        type="primary"
        htmlType="submit"
        disabled={!submittable}
        onClick={submittable && submitPassword}
      >
        {children}
      </Button>
    );
  };

  const revertDataOnCancel = () => {
    setOpenReset(false);
    if (userDetails.admin_access) getUserTableDetails();
  };

  return (
    <div className="user-management">
      {/* ---------------- SEARCH + ADD USER ---------------- */}
      <Row style={{ width: "100%", margin: "5px" }}>
        <Col xs={24} lg={12}>
          <Input.Search
            value={state.searchTex}
            style={{ width: "100%" }}
            size="large"
            placeholder="Search"
            onChange={onSearch}
          />
        </Col>
        <Col xs={24} lg={12} style={{ textAlign: "right", marginTop: 5 }}>
          {userDetails.admin_access && (
            <Button
              type="primary"
              onClick={() => navigate(`/admin/user/add`)}
            >
              Add User
            </Button>
          )}
        </Col>
      </Row>

      {/* ---------------- TABLE ---------------- */}
      <div
        className={classNames("", {
          "survey-container":
            windowSize$ === "lg" || windowSize$ === "xl" || windowSize$ === "xxl",
          "survey-container-small":
            windowSize$ === "xs" || windowSize$ === "sm" || windowSize$ === "md",
        })}
      >
        <Table
          key={tableKey}
          columns={userManagementTableData.headerContent || []}
          dataSource={state.data || []}
          pagination={false}
          bordered
          scroll={{ y: "calc(100vh - 330px)" }}
        />
      </div>

      {/* ---------------- PASSWORD MODAL ---------------- */}
      <Modal
        title="Reset password"
        centered
        open={openRest}
        onCancel={() => setOpenReset(false)}
        footer={[
          <Button key="cancel" onClick={revertDataOnCancel}>
            Cancel
          </Button>,
          <SubmitButton form={form}>Save</SubmitButton>,
        ]}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="oldPassword"
            label="Old Password"
            tooltip={{
              title: "Not required for resetting password for the first time",
              icon: <InfoCircleOutlined />,
            }}
          >
            <Input.Password />
          </Form.Item>
          <Form.Item
            name="password"
            label="Password"
            rules={[{ required: true, message: "Please input your password!" }]}
            hasFeedback
          >
            <Input.Password />
          </Form.Item>
          <Form.Item
            name="confirm"
            label="Confirm Password"
            dependencies={["password"]}
            hasFeedback
            rules={[
              { required: true, message: "Please confirm your password!" },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("password") === value) return Promise.resolve();
                  return Promise.reject(new Error("Passwords do not match!"));
                },
              }),
            ]}
          >
            <Input.Password />
          </Form.Item>
        </Form>
      </Modal>

      {/* ---------------- ENABLE PASSWORD MODAL ---------------- */}
      <Modal
        title="Modal"
        open={EnablePasswordModal}
        onOk={() => setEnablePasswordModal(false)}
        onCancel={() => setEnablePasswordModal(false)}
      >
        <p>Please make sure to add password for enabling the password login.</p>
      </Modal>

      {/* ---------------- SSO CONFIRM MODAL ---------------- */}
      <Modal
        title="Modal"
        open={enableSSOLogin}
        onOk={confirmSSOLogin}
        onCancel={cancelSSOLogin}
      >
        <p>
          Are you sure you want to <b>{pendingSSO.value ? "enable" : "disable"}</b> SSO login for this user?
        </p>
      </Modal>
    </div>
  );
};

export default UserManagement;